import React, { useCallback, useEffect, useState } from "react";
import Datetime from "react-datetime";
import {
  Checkbox,
  Divider,
  Form,
  Header,
  Icon,
  Input,
} from "semantic-ui-react";
import CustomFieldDropdown from "../../components/CustomFieldDropdown";
import CustomFieldGroupDropdown from "../../components/CustomFieldGroupDropdown";
import { mapCustomFieldValues } from "../../helpers/customFields";
import "../../styles/datetime.scss";
import CustomFieldGroupsService from "../../services/CustomFieldGroups";
import {
  TABLE_FORMAT_REGEX,
  CUSTOM_FIELD_ERROR_MESSAGES,
} from "../../constants/CustomFields";
import moment from "moment";

export function CustomFieldFormField({
  customFieldConfig,
  customFieldConfigs,
  onUpdateCustomFieldConfigs,
}) {
  const [fieldErrors, setFieldErrors] = useState({});

  const handleUpdateCustomFieldConfig = (key, value) => {
    const newCustomFieldConfigs = customFieldConfigs.map(cfc => {
      cfc.value = cfc.label === key ? value : cfc.value;
      return cfc;
    });
    onUpdateCustomFieldConfigs(newCustomFieldConfigs);
  };

  const handleChange = (event, data) => {
    const key = data.name;

    let newValue = data.value;
    if (data.type === "checkbox") {
      newValue = data.checked;
    }
    handleUpdateCustomFieldConfig(key, newValue);
  };

  const handleDateChange = (value, name) => {
    if (moment.isMoment(value)) {
      handleUpdateCustomFieldConfig(name, value.format("YYYY-MM-DD"));
    } else {
      handleUpdateCustomFieldConfig(name, value);
    }
  };

  const validateField = (fieldConfig, value) => {
    switch (fieldConfig.field_type) {
      case "table":
        return !value || TABLE_FORMAT_REGEX.test(value);
      // Add more field type validations here in the future
      default:
        return true;
    }
  };

  const handleFieldBlur = (fieldConfig, value) => {
    const isValid = validateField(fieldConfig, value);
    setFieldErrors(prev => ({ ...prev, [fieldConfig.label]: !isValid }));
  };

  const handleRemoveCustomFieldConfig = cf => {
    const newCustomFieldConfigs = customFieldConfigs.map(cfc => ({
      ...cfc,
      selected: cfc.id === cf.id ? false : cfc.selected,
    }));
    onUpdateCustomFieldConfigs(newCustomFieldConfigs);
  };

  const makeFormElement = cf => {
    cf.value = cf.value || "";
    switch (cf.field_type) {
      case "text":
        return (
          <Form.Input
            inline
            name={cf.label}
            label={cf.label}
            required={cf.required}
            onChange={handleChange}
            value={cf.value}
          />
        );
      case "numeric":
        return (
          <Form.Input
            inline
            type="number"
            min="0"
            name={cf.label}
            label={cf.label}
            required={cf.required}
            value={cf.value}
            onChange={handleChange}
          />
        );
      case "boolean":
        return (
          <Form.Field inline required={cf.required}>
            <label>{cf.label}</label>
            <Checkbox
              toggle
              name={cf.label}
              checked={cf.value === "false" ? false : cf.value}
              onChange={handleChange}
            />
          </Form.Field>
        );
      case "select":
        let multiSelect = cf.field_options.type === "multi";
        return (
          <Form.Select
            selectOnBlur={false}
            inline
            clearable
            search
            multiple={multiSelect}
            name={cf.label}
            label={cf.label}
            required={cf.required}
            options={cf.field_options["options"]}
            value={
              multiSelect
                ? cf.value || []
                : Array.isArray(cf.value)
                  ? cf.value[0]
                  : cf.value
            }
            onChange={handleChange}
          />
        );
      case "date":
        return (
          <Form.Field required={cf.required} inline>
            <label>{cf.label}</label>
            <Datetime
              closeOnSelect={true}
              name={cf.label}
              value={cf.value}
              onChange={value => {
                handleDateChange(value, cf.label);
              }}
              renderInput={props => (
                <Input icon="large calendar outline" {...props} />
              )}
              dateFormat={"YYYY-MM-DD"}
              timeFormat={false}
              className="tw:w-auto"
            />
          </Form.Field>
        );
      case "list":
        return (
          <Form.Select
            selectOnBlur={false}
            inline
            clearable
            search
            multiple
            name={cf.label}
            label={cf.label}
            required={cf.required}
            options={cf.value.map(val => ({ key: val, value: val, text: val }))}
            value={cf.value}
            onChange={handleChange}
            allowAdditions
          />
        );
      case "table":
        const hasError = fieldErrors[cf.label];

        return (
          <Form.Field inline required={cf.required} error={hasError}>
            <label>{cf.label} (Table Format)</label>
            <Form.TextArea
              name={cf.label}
              value={cf.value}
              onChange={handleChange}
              onBlur={e => handleFieldBlur(cf, e.target.value)}
              placeholder='Column1, Column2, Column3; Row1Value1, Row1Value2, Row1Value3; "Name with, comma", "Another, value"'
              style={{ minHeight: 100 }}
            />
            <div style={{ fontSize: "0.8em", color: "#666", marginTop: "5px" }}>
              Format: Column1, Column2, ...; Row1Value1, Row1Value2, ...;
              Row2Value1, Row2Value2, ...
              <br />
              Use quotes for cells with commas: "Cell with, comma", Regular cell
            </div>
            {hasError && (
              <div
                style={{
                  fontSize: "0.8em",
                  color: "#e03d3d",
                  marginTop: "5px",
                }}
              >
                ❌ {CUSTOM_FIELD_ERROR_MESSAGES.TABLE}
              </div>
            )}
          </Form.Field>
        );
      default:
        return null;
    }
  };

  return (
    <Form.Group inline>
      {makeFormElement(customFieldConfig)}
      {!customFieldConfig.required && (
        <Form.Field>
          <Icon
            link
            name="cancel"
            title="Remove Custom Field"
            onClick={() => handleRemoveCustomFieldConfig(customFieldConfig)}
          />
        </Form.Field>
      )}
    </Form.Group>
  );
}

export default function CustomFieldForm({
  modelType,
  customFields,
  customFieldConfigs,
  onUpdateCustomFieldConfigs,
}) {
  const [mappedCustomFieldConfigs, setMappedCustomFieldConfigs] = useState([]);
  const [customFieldGroups, setCustomFieldGroups] = useState([]);

  const fetchCustomFieldGroups = useCallback(async () => {
    let fetchedCustomFieldGroups =
      await CustomFieldGroupsService.getCustomFieldGroupsForModelType(
        modelType
      );
    setCustomFieldGroups(fetchedCustomFieldGroups);
  }, [modelType]);

  useEffect(() => {
    if (modelType) {
      fetchCustomFieldGroups();
    }
  }, [modelType]);

  useEffect(() => {
    setMappedCustomFieldConfigs([
      ...mapCustomFieldValues(customFields, customFieldConfigs),
    ]);
  }, [customFields, customFieldConfigs]);

  return mappedCustomFieldConfigs.length > 0 ? (
    <>
      <Divider hidden />
      <Header className="section-header ui form sub-header">
        Custom Fields
      </Header>
      <Form.Group>
        <Form.Field>
          <CustomFieldDropdown
            customFieldConfigs={mappedCustomFieldConfigs}
            onUpdateCustomFieldConfigs={onUpdateCustomFieldConfigs}
          />
        </Form.Field>
        {customFieldGroups.length > 0 && (
          <Form.Field>
            <CustomFieldGroupDropdown
              customFieldGroups={customFieldGroups}
              customFieldConfigs={mappedCustomFieldConfigs}
              onCustomFieldGroupSelect={onUpdateCustomFieldConfigs}
            />
          </Form.Field>
        )}
      </Form.Group>
      {mappedCustomFieldConfigs
        .filter(cfc => cfc.required || cfc.selected)
        .map(customFieldConfig => (
          <Form.Field key={customFieldConfig.label}>
            <CustomFieldFormField
              customFieldConfig={customFieldConfig}
              customFieldConfigs={mappedCustomFieldConfigs}
              onUpdateCustomFieldConfigs={onUpdateCustomFieldConfigs}
            />
          </Form.Field>
        ))}
    </>
  ) : null;
}
